/**
 * FacturePublique — Page publique accessible par lien (sans auth)
 *
 * 🆕 [Phase 77 — 2026-06-21] Refonte mobile-first complète :
 *   - Toutes les inline `style={...}` converties en classes Tailwind responsive (sm:/md:/lg:)
 *   - Header logo + entreprise + numéros : 1 col mobile → 3 cols desktop
 *   - Section client : 1 col mobile → 3 cols desktop
 *   - Tableaux items : `<table>` desktop + cartes empilées mobile (espacement aéré)
 *   - Bouton « Télécharger PDF » : sticky-bottom plein écran mobile, statique desktop
 *   - Fonts body ≥ 16px (text-base) pour bloquer le zoom iOS auto
 *   - Anti dark mode Samsung : 3 verrous (colorScheme:light only inline, classes
 *     `bg-white text-slate-900` explicites, meta theme-color via useEffect)
 *   - PDF html2pdf.js : `windowWidth: 1024, windowHeight: 768` pour forcer la
 *     capture en viewport virtuelle desktop (PDF reste impeccable même si l'utilisateur
 *     télécharge depuis son mobile)
 */
import { useRef, useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useParams, useSearchParams } from 'react-router-dom';
import { usePublicFetch } from '../hooks/usePublicFetch';
import { LienRemplace } from '../components/public/LienRemplace';
import { ErreurPublique } from '../components/public/ErreurPublique';
import { calculerTotauxFacture } from '../lib/facturePubliqueTotaux'; // 🚨 lien remplacé → demande d'un nouveau
import { signalerEvenementPublic } from '../lib/suiviPublic'; // 📬 Suivi client lien

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function fmt(v) {
  return new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD' }).format(v || 0);
}
function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-CA', { day: 'numeric', month: 'long', year: 'numeric' });
}
function fmtDateShort(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('fr-CA'); } catch { return ''; }
}

// 🆕 [Phase 77] Composant interne : ligne d'item en mode CARTE EMPILÉE (mobile).
// Affiché uniquement via `md:hidden`. Le tableau classique reste `hidden md:table`
// pour la version desktop et pour le PDF (windowWidth:1024 active md:).
function MobileItemCard({ label, value, valueClassName = '', dataTestid }) {
  return (
    <div className="flex justify-between items-baseline gap-3 py-1" data-testid={dataTestid}>
      <span className="text-sm text-slate-600">{label}</span>
      <span className={`text-sm font-medium text-slate-900 ${valueClassName}`}>{value}</span>
    </div>
  );
}

export default function FacturePublique() {
  const { facture_id } = useParams();
  const [searchParams] = useSearchParams();
  const jeton = searchParams.get('t');  // 🧱 Phase 4 — Lot C (D3) : jeton de lien public (sans expiration, révocable)
  const pdfRootRef = useRef(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // 🆕 [Phase 77] Anti dark mode Samsung — injecte un <meta name="theme-color">
  // dans le <head> qui force le navigateur à utiliser une chrome bar claire et
  // signale clairement au système OneUI/Force Dark de ne pas inverser les couleurs.
  useEffect(() => {
    const existing = document.querySelector('meta[name="theme-color"]');
    const created = !existing;
    let meta = existing;
    if (created) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'theme-color');
      document.head.appendChild(meta);
    }
    const previousContent = meta.getAttribute('content');
    meta.setAttribute('content', '#ffffff');
    return () => {
      if (created) {
        document.head.removeChild(meta);
      } else if (previousContent !== null) {
        meta.setAttribute('content', previousContent);
      }
    };
  }, []);

  const handleDownloadPdf = async () => {
    if (!pdfRootRef.current || downloadingPdf) return;
    setDownloadingPdf(true);
    try {
      const html2pdf = (await import('html2pdf.js')).default;
      const numero = (data?.facture?.numero_facture || 'Facture').replace(/[^a-z0-9\-_]/gi, '_');
      // 🆕 [Phase 77 Fix 1 — 2026-06-22] Détection mobile pour adapter `scale`.
      // Sur Samsung Android (et autres mobiles), scale:2 + windowWidth:1024 +
      // windowHeight:768 fait un canvas ~2048×1536 → OOM ou erreur de génération.
      // Fix : scale 1.5 sur mobile (garde la lisibilité), 2 sur desktop. On retire
      // aussi windowHeight (auto = laisse html2canvas calculer la hauteur réelle).
      const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
      await html2pdf()
        .from(pdfRootRef.current)
        .set({
          margin: [10, 10, 10, 10],
          filename: `Facture_${numero}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: {
            scale: isMobile ? 1.5 : 2,
            useCORS: true,
            backgroundColor: '#ffffff',
            windowWidth: 1024,
          },
          jsPDF: { unit: 'mm', format: 'letter', orientation: 'portrait' },
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
        })
        .save();
      signalerEvenementPublic(`/api/crm/factures/${facture_id}/view-public/evenement?t=${encodeURIComponent(jeton || '')}`, 'pdf');  // 📬 Suivi client
    } catch (e) {
      console.error('[Phase 77 Fix 1] Erreur html2pdf:', e);
      // 🆕 [Phase 77 Fix 1] Message clair en français + fallback impression navigateur.
      toast.error("Le PDF n'a pas pu être généré sur cet appareil — utilisez Imprimer (puis « Enregistrer en PDF »).", { duration: 8000 });
    } finally {
      setDownloadingPdf(false);
    }
  };

  const { data, loading, error } = usePublicFetch(
    facture_id ? `/api/crm/factures/${facture_id}/view-public?t=${encodeURIComponent(jeton || '')}` : null,
    { notFoundMsg: 'Facture introuvable' }
  );

  if (loading) return (
    <div
      className="min-h-screen flex items-center justify-center bg-slate-100"
      style={{ colorScheme: 'light only', WebkitTextSizeAdjust: '100%' }}
    >
      <div className="text-center">
        <div className="w-10 h-10 border-[3px] border-slate-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-3" />
        <p className="text-slate-500 text-sm">Chargement de la facture...</p>
      </div>
    </div>
  );

  if (error) return (
    <div
      className="min-h-screen flex items-center justify-center bg-slate-100 p-4"
      style={{ colorScheme: 'light only', WebkitTextSizeAdjust: '100%' }}
    >
      {(typeof error === 'object' && error?.code === 'lien_remplace') ? (
        <LienRemplace factureId={facture_id} message={error.message} />
      ) : (
        <ErreurPublique error={error} doc="Facture" onRetry={() => window.location.reload()} />
      )}
    </div>
  );

  const facture = data.facture || {};
  const factureDepot = data.facture_depot || null;
  const entreprise = data.entreprise || {};
  const client = data.client || {};
  const dossier = data.dossier || {};
  const items = facture.items || [];
  const ajouts = data.ajouts || [];
  const isDepot = facture.type_facture === 'depot';
  const isFinaleOrComp = facture.type_facture === 'finale' || facture.type_facture === 'complementaire';
  const titre = isDepot ? 'FACTURE DE DEPOT'
    : facture.type_facture === 'complementaire'
      ? `FACTURE COMPLEMENTAIRE${facture.numero_complementaire ? ' #' + facture.numero_complementaire : ''}`
      : 'FACTURE FINALE';

  const rabaisMontant = parseFloat(facture.rabais_montant) || 0;
  const rabaisDescription = (facture.rabais_description || '').trim();
  const hasRabais = rabaisMontant > 0;

  const itemsLocation = items.filter(it => (it.type || '').toLowerCase() === 'equipement');
  const itemsPeinture = items.filter(it => (it.type || '').toLowerCase() !== 'equipement');
  const totalAjouts = ajouts.reduce((s, a) => s + (parseFloat(a.montant_total) || 0), 0);

  const montantDepotPaye = factureDepot ? (factureDepot.montant_paye_reel || factureDepot.montant_paye || 0) : 0;
  const montantDepotDemande = factureDepot ? (factureDepot.montant_total || 0) : 0;
  const depotEstPaye = factureDepot && (factureDepot.statut === 'paye' || montantDepotPaye > 0);
  const montantDepotASoustraire = montantDepotPaye > 0 ? montantDepotPaye : montantDepotDemande;
  const datePaiementDepot = factureDepot?.date_paiement_reel || factureDepot?.date_paiement || null;

  const logoUrl = entreprise.logo_url || (entreprise.logo_principal ? `${API_URL}${entreprise.logo_principal}` : null);
  const partenairesUrls = entreprise.partenaires_urls || [];

  // 🎯 [Phase v13.49 — 2026-07-20] Calcul totaux avec fix cash hybride (Bug B).
  // Quand la facture a été créée en mode cash hybride (`v13_49_cash_hybride_applique`),
  // les items représentent le contrat COMPLET (7 500 $) mais la facture officielle
  // n'a été taxée que sur la partie officielle (4 000 $ HT). Dans ce cas, on utilise
  // les vrais montants persistés sur la facture au lieu de recalculer depuis les items.
  //
  // Décision utilisatrice 2026-07-20 : la mention de la portion cash N'APPARAÎT PAS
  // sur le PDF public (position juridique/fiscale). Elle est visible uniquement
  // dans les vues internes owner-only (OngletFactures).
  // 🚨 Totaux = même source que la facture interne / le PDF (montants persistés) ; items seulement en repli (lib/facturePubliqueTotaux.js)
  const totaux = calculerTotauxFacture(facture, items, rabaisMontant);
  const rabaisAffiche = totaux.rabaisAffiche;
  const sousTotal = totaux.sousTotal, baseTaxable = totaux.baseTaxable, tpsContrat = totaux.tps, tvqContrat = totaux.tvq, totalContrat = totaux.total;
  const pctDepot = facture.pourcentage_depot || (facture.depot_type === 'pourcentage' ? facture.depot_valeur : null);
  const montantDepot = facture.montant_total || (pctDepot ? totalContrat * pctDepot / 100 : totalContrat);

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { size: letter; margin: 0.5in; }
          html, body { height: auto !important; overflow: visible !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; background: white !important; }
          #root { position: static !important; overflow: visible !important; height: auto !important; }
          .facture-wrapper { min-height: auto !important; background: white !important; padding: 0 !important; }
          .facture-card { box-shadow: none !important; border-radius: 0 !important; max-width: none !important; }
          div[style*="position: fixed"], div[style*="position:fixed"],
          [data-emergent-badge], #emergent-badge, .emergent-badge,
          a[href*="emergentagent"], a[href*="emergent"] { display: none !important; visibility: hidden !important; }
        }
        /* 🆕 [Phase 77 Fix 1 — 2026-06-22] Masquer le badge « Made with Emergent »
           UNIQUEMENT sur la facture publique (preserve le badge sur les pages
           internes admin → respecte les ToS Emergent). Sélecteurs combinés pour
           capturer toutes les variantes possibles d'injection. */
        #emergent-badge,
        [id^="emergent-"],
        [data-emergent-badge],
        a[href*="emergentagent.com"],
        a[href*="emergent.sh"],
        a[href*="app.emergent.sh"] {
          display: none !important;
          visibility: hidden !important;
          pointer-events: none !important;
        }
      `}</style>
      {/* 🆕 [Phase 77] Wrapper root : `bg-white text-slate-900` explicite + inline
          `colorScheme:'light only'` + `WebkitTextSizeAdjust:100%` empêchent le force-dark
          Samsung/Chrome Android et le zoom iOS auto sur les inputs. */}
      <div
        className="facture-wrapper min-h-screen bg-slate-100 px-3 py-5 sm:px-4 sm:py-6 md:py-8 pb-24 md:pb-8 text-slate-900"
        style={{ colorScheme: 'light only', WebkitTextSizeAdjust: '100%' }}
        data-pdf-ready="1"
        data-testid="facture-publique-root"
      >
        {/* Barre du haut — bouton Télécharger en PDF (desktop). Sur mobile,
            cette barre garde son bouton mais on en ajoute un autre sticky-bottom
            ci-dessous pour être au pouce. */}
        <div className="no-print max-w-4xl mx-auto mb-3 hidden md:flex justify-end gap-2">
          <button
            onClick={handleDownloadPdf}
            disabled={downloadingPdf}
            data-testid="btn-download-pdf-desktop"
            className={`px-5 py-2 text-white text-sm font-semibold rounded-lg border-none transition shadow-sm ${
              downloadingPdf
                ? 'bg-slate-400 cursor-not-allowed'
                : 'bg-emerald-600 hover:bg-emerald-700 cursor-pointer'
            }`}
          >
            {downloadingPdf ? 'Génération…' : '📥 Télécharger en PDF'}
          </button>
        </div>

        {/* Card document — `bg-white` explicite empêche le force-dark Samsung */}
        <div
          ref={pdfRootRef}
          className="facture-card max-w-4xl mx-auto bg-white shadow-sm rounded-lg md:rounded-xl overflow-hidden"
          data-testid="pdf-content-root"
        >
          <div className="p-4 sm:p-6 md:p-8 space-y-4 md:space-y-5 text-base">

            {/* HEADER — Logo + Entreprise + Numéros */}
            <section className="border border-slate-200 rounded-lg p-4 sm:p-5" data-testid="facture-header">
              <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr_1fr] gap-5 md:gap-6 items-start">
                {/* Logo */}
                <div className="flex justify-center md:justify-start">
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt="Logo"
                      className="w-full max-w-[220px] md:max-w-[280px] h-auto object-contain"
                      data-testid="facture-logo-img"
                    />
                  ) : (
                    // 🆕 [Phase 77 Fix 1 — 2026-06-22] Fallback logo : nom de l'entreprise
                    // sobre sur 3 lignes en vert PRQC (au lieu du placeholder gris "Logo").
                    <div
                      className="text-2xl md:text-3xl font-bold text-emerald-700 leading-tight text-center md:text-left"
                      data-testid="facture-logo-fallback"
                    >
                      Peinture<br />Résidentielle<br />Qc Inc.
                    </div>
                  )}
                </div>

                {/* Infos entreprise */}
                <div className="text-center">
                  <h2 className="text-lg md:text-xl font-bold mb-2">{entreprise.nom_entreprise}</h2>
                  <div className="text-sm leading-relaxed text-slate-700 space-y-0.5">
                    <p>{entreprise.adresse}</p>
                    <p>{entreprise.ville} {entreprise.code_postal}</p>
                    <p className="font-semibold mt-1.5">
                      {entreprise.telephone && (
                        <a href={`tel:${entreprise.telephone}`} className="text-slate-900 hover:text-blue-600 transition no-underline">{entreprise.telephone}</a>
                      )}
                    </p>
                    {entreprise.email && (
                      <p className="break-all">
                        <a href={`mailto:${entreprise.email}`} className="text-slate-700 hover:text-blue-600 transition no-underline">{entreprise.email}</a>
                      </p>
                    )}
                    {entreprise.site_web && <p className="break-all">{entreprise.site_web}</p>}
                  </div>
                </div>

                {/* Numéros facture + TPS/TVQ/RBQ */}
                <div className="text-sm md:text-xs space-y-1 border-t md:border-t-0 md:border-l border-slate-200 pt-3 md:pt-0 md:pl-4">
                  <p className="font-bold text-base md:text-sm">Facture : <span className="break-all">{facture.numero_facture}</span></p>
                  <p><strong className="font-semibold">Date :</strong> {fmtDate(facture.date_creation)}</p>
                  <div className="mt-2 pt-2 border-t border-slate-200 space-y-0.5 text-sm md:text-[11px] text-slate-600">
                    {entreprise.numero_tps && <p><strong className="font-semibold text-slate-700">TPS :</strong> {entreprise.numero_tps}</p>}
                    {entreprise.numero_tvq && <p><strong className="font-semibold text-slate-700">TVQ :</strong> {entreprise.numero_tvq}</p>}
                    {entreprise.numero_rbq && <p><strong className="font-semibold text-slate-700">RBQ :</strong> {entreprise.numero_rbq}</p>}
                    {entreprise.numero_neq && <p><strong className="font-semibold text-slate-700">NEQ :</strong> {entreprise.numero_neq}</p>}
                  </div>
                </div>
              </div>
            </section>

            {/* TITRE */}
            <h1 className="text-2xl md:text-3xl font-bold text-center text-slate-900 my-4 md:my-6 tracking-tight" data-testid="facture-titre">
              {titre}
            </h1>

            {/* CLIENT + ADRESSE FACTURATION + PROJET */}
            <section className="border border-slate-200 rounded-lg p-4 sm:p-5" data-testid="facture-client-section">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 text-sm">
                <div>
                  <p className="font-bold text-base mb-1.5">Facturé à :</p>
                  <p className="font-semibold text-slate-900 mb-1 break-words">{client.nom || facture.client_nom}</p>
                  {client.telephone && (
                    <p className="text-slate-600">
                      Téléphone : <a href={`tel:${client.telephone}`} className="text-slate-700 hover:text-blue-600 transition no-underline">{client.telephone}</a>
                    </p>
                  )}
                  {client.email && (
                    <p className="text-slate-600 break-all">
                      Courriel : <a href={`mailto:${client.email}`} className="text-slate-700 hover:text-blue-600 transition no-underline">{client.email}</a>
                    </p>
                  )}
                </div>
                <div>
                  <p className="font-bold text-base mb-1.5">Adresse de facturation :</p>
                  <p className="text-slate-700 break-words">{facture.adresse_facturation || client.adresse_client || '—'}</p>
                  <p className="text-slate-700">
                    {facture.ville_facturation || client.ville_client || ''}{' '}
                    {facture.code_postal_facturation || client.code_postal_client || ''}
                  </p>
                </div>
                <div>
                  <p className="font-bold text-base mb-1.5">Projet :</p>
                  <p className="text-slate-700 break-words">{dossier.nom_dossier || '—'}</p>
                  {dossier.representant_nom && (
                    <p className="text-slate-600 mt-1 text-sm">Représentant : {dossier.representant_nom}</p>
                  )}
                </div>
              </div>
            </section>

            {/* SECTION PEINTURE / SERVICES */}
            {itemsPeinture.length > 0 && (
              <section data-testid="facture-section-peinture" className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="bg-indigo-50 px-4 py-2.5 border-b border-indigo-100 font-bold text-sm text-indigo-900 uppercase tracking-wider">
                  Travaux de peinture
                </div>
                {/* DESKTOP : table classique */}
                <table className="hidden md:table w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b-2 border-slate-200">
                      <th className="px-4 py-2.5 text-left font-semibold uppercase text-xs tracking-wider">Description</th>
                      <th className="px-4 py-2.5 text-center font-semibold uppercase text-xs">Qté</th>
                      <th className="px-4 py-2.5 text-right font-semibold uppercase text-xs">Prix unit.</th>
                      <th className="px-4 py-2.5 text-right font-semibold uppercase text-xs">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {itemsPeinture.map((item, i) => (
                      <tr key={item.id || item._id || `peinture-${i}`} className="border-b border-slate-100">
                        <td className="px-4 py-3 whitespace-pre-wrap">{item.description}</td>
                        <td className="px-4 py-3 text-center">{item.quantite || 1}</td>
                        <td className="px-4 py-3 text-right">{fmt(item.prix_unitaire || item.taux || item.montant)}</td>
                        <td className="px-4 py-3 text-right font-semibold">{fmt(item.montant || (item.prix_unitaire || 0) * (item.quantite || 1))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* MOBILE : cartes empilées */}
                <div className="md:hidden divide-y divide-slate-100">
                  {itemsPeinture.map((item, i) => (
                    <div key={item.id || item._id || `peinture-m-${i}`} className="p-4 space-y-1.5">
                      <p className="font-semibold text-slate-900 whitespace-pre-wrap break-words text-base">{item.description}</p>
                      <MobileItemCard label="Quantité" value={item.quantite || 1} />
                      <MobileItemCard label="Prix unitaire" value={fmt(item.prix_unitaire || item.taux || item.montant)} />
                      <MobileItemCard label="Total" value={fmt(item.montant || (item.prix_unitaire || 0) * (item.quantite || 1))} valueClassName="font-bold text-base" />
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* SECTION LOCATION D'ÉQUIPEMENT */}
            {itemsLocation.length > 0 && (
              <section data-testid="facture-section-location" className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="bg-cyan-50 px-4 py-2.5 border-b border-cyan-100 font-bold text-sm text-cyan-900 uppercase tracking-wider">
                  Location d&apos;équipement
                </div>
                {/* DESKTOP */}
                <table className="hidden md:table w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b-2 border-slate-200">
                      <th className="px-4 py-2.5 text-left font-semibold uppercase text-xs">Description</th>
                      <th className="px-4 py-2.5 text-right font-semibold uppercase text-xs w-32">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {itemsLocation.map((item, i) => {
                      const prixLoc = parseFloat(item.prix_location) || 0;
                      const qte = parseFloat(item.quantite) || 1;
                      const prixLivr = parseFloat(item.prix_livraison) || 0;
                      const extrasList = Array.isArray(item.extras) ? item.extras : [];
                      return (
                        <tr key={item.id || item._id || `loc-${i}`} className="border-b border-slate-100">
                          <td className="px-4 py-3 align-top">
                            <div className="font-semibold text-slate-900">{item.description || '—'}</div>
                            {(item.date_prise || item.date_remise) && (
                              <div className="text-xs text-slate-500 mt-1">
                                Période : {fmtDate(item.date_prise)} → {fmtDate(item.date_remise)} · Qté : {qte}
                              </div>
                            )}
                            <div className="mt-1.5 pl-3 border-l-2 border-slate-200 text-sm text-slate-700 leading-relaxed space-y-0.5">
                              {prixLoc > 0 && (
                                <div className="flex justify-between gap-2">
                                  <span>· Location ({item.duree_type || 'jour'}{qte > 1 ? ` × ${qte}` : ''})</span>
                                  <span>{fmt(prixLoc * qte)}</span>
                                </div>
                              )}
                              {prixLivr > 0 && (
                                <div className="flex justify-between gap-2">
                                  <span>· Livraison</span>
                                  <span>{fmt(prixLivr)}</span>
                                </div>
                              )}
                              {extrasList.map((ex, j) => (
                                <div key={`ex-${i}-${j}`} className="flex justify-between gap-2">
                                  <span>· {ex.description || 'Extra'}</span>
                                  <span>{fmt(ex.montant)}</span>
                                </div>
                              ))}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right font-bold align-top">{fmt(item.montant)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {/* MOBILE */}
                <div className="md:hidden divide-y divide-slate-100">
                  {itemsLocation.map((item, i) => {
                    const prixLoc = parseFloat(item.prix_location) || 0;
                    const qte = parseFloat(item.quantite) || 1;
                    const prixLivr = parseFloat(item.prix_livraison) || 0;
                    const extrasList = Array.isArray(item.extras) ? item.extras : [];
                    return (
                      <div key={item.id || item._id || `loc-m-${i}`} className="p-4 space-y-2">
                        <p className="font-semibold text-slate-900 break-words text-base">{item.description || '—'}</p>
                        {(item.date_prise || item.date_remise) && (
                          <p className="text-sm text-slate-500">
                            Période : {fmtDate(item.date_prise)} → {fmtDate(item.date_remise)} · Qté : {qte}
                          </p>
                        )}
                        <div className="pl-3 border-l-2 border-slate-200 space-y-1 text-sm text-slate-700">
                          {prixLoc > 0 && (
                            <div className="flex justify-between gap-2">
                              <span>· Location ({item.duree_type || 'jour'}{qte > 1 ? ` × ${qte}` : ''})</span>
                              <span>{fmt(prixLoc * qte)}</span>
                            </div>
                          )}
                          {prixLivr > 0 && (
                            <div className="flex justify-between gap-2">
                              <span>· Livraison</span>
                              <span>{fmt(prixLivr)}</span>
                            </div>
                          )}
                          {extrasList.map((ex, j) => (
                            <div key={`ex-m-${i}-${j}`} className="flex justify-between gap-2">
                              <span>· {ex.description || 'Extra'}</span>
                              <span>{fmt(ex.montant)}</span>
                            </div>
                          ))}
                        </div>
                        <div className="flex justify-between items-baseline gap-2 pt-2 border-t border-slate-100">
                          <span className="text-sm text-slate-600">Total équipement</span>
                          <span className="text-lg font-bold text-slate-900">{fmt(item.montant)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* SECTION AJOUTS PENDANT LES TRAVAUX */}
            {isFinaleOrComp && ajouts.length > 0 && (
              <section data-testid="facture-section-ajouts" className="border border-amber-600 rounded-lg overflow-hidden bg-amber-50">
                <div className="bg-amber-100 px-4 py-2.5 border-b border-amber-300 font-bold text-sm text-amber-900 uppercase tracking-wider">
                  Ajouts pendant les travaux
                </div>
                <div className="p-4 space-y-4">
                  {ajouts.map((ajout, i) => (
                    <div
                      key={ajout.id || `ajout-${i}`}
                      data-testid={`facture-ajout-${i}`}
                      className={`${i < ajouts.length - 1 ? 'border-b border-dashed border-amber-300 pb-3' : ''}`}
                    >
                      {ajout.description && (
                        <p data-testid={`facture-ajout-description-${i}`} className="font-semibold text-sm text-amber-900 mb-2">
                          {ajout.description}
                        </p>
                      )}
                      {(ajout.items || []).length > 0 && (
                        <div className="space-y-1">
                          {(ajout.items || []).map((it, j) => (
                            <div
                              key={it.id || `aj-it-${i}-${j}`}
                              className="flex justify-between gap-2 text-sm py-1 border-b border-amber-200 last:border-b-0"
                            >
                              <span className="break-words text-slate-800 flex-1">{it.description || '—'}</span>
                              <span className="font-semibold text-slate-900 whitespace-nowrap">{fmt(it.montant)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      <p className="text-right font-bold text-sm mt-2 text-amber-900">
                        Total ajout : {fmt(ajout.montant_total)}
                      </p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* TOTAUX — pleine largeur sur mobile, alignés à droite desktop */}
            <section className="flex justify-end" data-testid="facture-totaux">
              <div className="w-full md:w-80 space-y-1">
                <div className="flex justify-between text-sm py-1.5">
                  <span className="text-slate-700">Sous-total</span>
                  <span className="font-medium">{fmt(sousTotal)}</span>
                </div>
                {hasRabais && (
                  <>
                    <div data-testid="facture-rabais-row" className="flex justify-between text-sm py-1.5 text-red-700 font-semibold">
                      <span>Rabais commercial</span>
                      <span>− {fmt(rabaisAffiche)}</span>
                    </div>
                    {rabaisDescription && (
                      <div data-testid="facture-rabais-description" className="text-xs italic text-slate-500 pb-1">
                        {rabaisDescription}
                      </div>
                    )}
                  </>
                )}
                <div className="flex justify-between text-sm py-1.5">
                  <span className="text-slate-700">TPS (5%)</span>
                  <span className="font-medium">{fmt(tpsContrat)}</span>
                </div>
                <div className="flex justify-between text-sm py-1.5">
                  <span className="text-slate-700">TVQ (9,975%)</span>
                  <span className="font-medium">{fmt(tvqContrat)}</span>
                </div>
                <div className="flex justify-between py-2.5 text-base font-bold border-t-2 border-slate-900">
                  <span>Total du contrat</span>
                  <span>{fmt(totalContrat)}</span>
                </div>
                {isFinaleOrComp && totalAjouts > 0 && (
                  <div data-testid="facture-total-ajouts-row" className="flex justify-between text-sm py-1.5 text-amber-700 font-semibold">
                    <span>+ Ajouts pendant les travaux</span>
                    <span>+ {fmt(totalAjouts)}</span>
                  </div>
                )}
                {isDepot && (
                  <div className="flex justify-between bg-blue-50 text-blue-700 px-3 py-2.5 text-base font-bold rounded mt-2">
                    <span>Dépôt demandé{pctDepot ? ` (${pctDepot}%)` : ''}</span>
                    <span>{fmt(montantDepot)}</span>
                  </div>
                )}
                {!isDepot && factureDepot && montantDepotASoustraire > 0 && (
                  <>
                    <div className="flex justify-between text-sm py-1.5 text-blue-700 font-semibold gap-2">
                      <span className="break-words">− Dépôt {depotEstPaye ? 'reçu' : 'demandé'} (PRQC){factureDepot.numero_facture ? ` · ${factureDepot.numero_facture}` : ''}</span>
                      <span className="whitespace-nowrap">− {fmt(montantDepotASoustraire)}</span>
                    </div>
                    <div className="flex justify-between bg-emerald-50 text-emerald-700 px-3 py-3 text-base md:text-lg font-extrabold rounded border-t-2 border-emerald-700 mt-1.5">
                      <span>SOLDE DÛ</span>
                      <span data-testid="facture-solde-du">{fmt(Math.max(0, totalContrat + totalAjouts - montantDepotASoustraire))}</span>
                    </div>
                    {/* Encadré récap dépôt */}
                    <div className="mt-3 border-2 border-blue-700 rounded-lg p-3 bg-blue-50">
                      <div className="flex justify-between items-center mb-2 gap-2">
                        <strong className="text-sm text-blue-900">💰 Dépôt — Peinture Résidentielle QC</strong>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full text-white whitespace-nowrap ${depotEstPaye ? 'bg-emerald-600' : 'bg-amber-500'}`}>
                          {depotEstPaye ? '✓ PAYÉ' : 'EN ATTENTE'}
                        </span>
                      </div>
                      <div className="text-sm leading-relaxed text-slate-700 space-y-0.5">
                        {factureDepot.numero_facture && (
                          <div className="flex justify-between gap-2">
                            <span>Facture de dépôt :</span>
                            <strong className="break-all">{factureDepot.numero_facture}</strong>
                          </div>
                        )}
                        {factureDepot.pourcentage_depot && (
                          <div className="flex justify-between gap-2">
                            <span>Pourcentage :</span>
                            <strong>{factureDepot.pourcentage_depot} %</strong>
                          </div>
                        )}
                        <div className="flex justify-between gap-2">
                          <span>Montant demandé :</span>
                          <strong className="text-blue-900">{fmt(montantDepotDemande)}</strong>
                        </div>
                        {montantDepotPaye > 0 && (
                          <div className="flex justify-between gap-2">
                            <span className="text-emerald-700">Montant reçu :</span>
                            <strong className="text-emerald-700">{fmt(montantDepotPaye)}</strong>
                          </div>
                        )}
                        {datePaiementDepot && (
                          <div className="flex justify-between gap-2">
                            <span>Date du paiement :</span>
                            <strong>{fmtDateShort(datePaiementDepot)}</strong>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </section>

            {/* REGLEMENT DE LA FACTURE */}
            <section className="border border-slate-200 rounded-lg p-4 sm:p-5" data-testid="facture-reglement">
              <h3 className="text-base font-bold mb-3">Règlement de la facture</h3>
              <p className="font-bold text-sm mb-1.5">Virement Interac :</p>
              <p className="text-sm leading-relaxed text-slate-700">
                Pour acquitter cette facture, vous pouvez effectuer un virement Interac à l&apos;adresse courriel de l&apos;entreprise :{' '}
                <strong className="break-all">
                  <a href={`mailto:${entreprise.email || 'info@peintureresidentielleqc.com'}`} className="text-slate-900 hover:text-blue-600 transition no-underline">
                    {entreprise.email || 'info@peintureresidentielleqc.com'}
                  </a>
                </strong>
              </p>
              <p className="text-sm mt-1 text-slate-700">Aucun mot de passe ne sera requis.</p>
              <p className="text-sm mt-1 text-slate-700">Nous comprenons que le paiement pourrait devoir être réparti sur plusieurs jours, selon la limite bancaire permise par votre institution.</p>
              <p className="font-bold text-sm mt-4 mb-1.5">Chèque :</p>
              <p className="text-sm leading-relaxed text-slate-700">Vous pouvez acquitter cette facture par chèque en l&apos;adressant à :</p>
              <p className="text-sm mt-1 text-slate-700 leading-relaxed">
                {entreprise.nom_entreprise || 'Peinture Residentielle Qc Inc.'}<br />
                {entreprise.adresse || ''}<br />
                {entreprise.ville || ''} {entreprise.code_postal || ''}
              </p>
            </section>

            {/* TERMES ET CONDITIONS */}
            <section className="border border-slate-200 rounded-lg p-4 sm:p-5" data-testid="facture-conditions">
              <h3 className="text-base font-bold mb-3">Termes et conditions</h3>
              <ul className="space-y-1.5 text-sm text-slate-700">
                <li className="border-b border-dotted border-slate-200 pb-1.5">Tous les prix ci-haut mentionnés sont en dollars Canadiens.</li>
                <li className="border-b border-dotted border-slate-200 pb-1.5">Paiement net 3 jours.</li>
                <li>Frais d&apos;administration de 2% par mois sur tout compte passé dû.</li>
              </ul>
            </section>

            {/* 🆕 [Phase 75] NOTES VISIBLES CLIENT */}
            {facture.notes_client && facture.notes_client.trim() && (
              <section
                data-testid="notes-client-encadre"
                className="border-2 border-emerald-500 bg-emerald-50 rounded-lg p-4 sm:p-5"
              >
                <h3 className="text-sm font-bold mb-2 text-emerald-900 uppercase tracking-wider">
                  Notes
                </h3>
                <p className="text-sm leading-relaxed text-emerald-900 whitespace-pre-wrap m-0">
                  {facture.notes_client}
                </p>
              </section>
            )}

            {/* REMERCIEMENTS */}
            <section className="border border-slate-200 rounded-lg p-4 sm:p-5 bg-emerald-50 text-center" data-testid="facture-remerciements">
              <p className="italic font-semibold text-sm md:text-base text-slate-700">
                Nous vous remercions de la confiance que vous nous accordez pour la réalisation de vos travaux !
              </p>
            </section>

            {/* PARTENAIRES */}
            {partenairesUrls.length > 0 && (
              <section className="border border-slate-200 rounded-lg p-4 sm:p-5 text-center" data-testid="facture-partenaires">
                <p className="text-base font-bold mb-3">Nos partenaires</p>
                <div className="flex items-center justify-center gap-5 flex-wrap">
                  {partenairesUrls.map((url, i) => (
                    <img
                      key={url.id || url._id || i}
                      src={url.startsWith('http') ? url : `${API_URL}${url}`}
                      alt={`Partenaire ${i + 1}`}
                      className="h-9 w-auto object-contain"
                    />
                  ))}
                </div>
              </section>
            )}

            {/* FOOTER */}
            <div className="text-center text-slate-400 text-xs pt-3 border-t border-slate-200" data-testid="facture-footer">
              {entreprise.nom_entreprise}
              {entreprise.telephone && (
                <> — <a href={`tel:${entreprise.telephone}`} className="text-slate-500 hover:text-blue-600 transition no-underline">{entreprise.telephone}</a></>
              )}
              {entreprise.email && (
                <> — <a href={`mailto:${entreprise.email}`} className="text-slate-500 hover:text-blue-600 transition no-underline break-all">{entreprise.email}</a></>
              )}
            </div>

          </div>
        </div>

        {/* 🆕 [Phase 77] CTA STICKY-BOTTOM MOBILE — bouton plein écran au pouce.
            Caché sur desktop (md:hidden) car le bouton du haut est visible. */}
        <div className="no-print md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] z-50">
          <button
            onClick={handleDownloadPdf}
            disabled={downloadingPdf}
            data-testid="btn-download-pdf-mobile"
            className={`w-full min-h-[48px] text-white text-base font-bold rounded-lg transition shadow-md ${
              downloadingPdf
                ? 'bg-slate-400 cursor-not-allowed'
                : 'bg-emerald-600 active:bg-emerald-700 cursor-pointer'
            }`}
          >
            {downloadingPdf ? 'Génération…' : '📥 Télécharger en PDF'}
          </button>
        </div>
      </div>
    </>
  );
}
